import type {
  ClassType,
  EquipmentType,
  ExerciseEntry,
  Journal,
} from "@vlab-corporation/vlab-pilates-service-interface/class";
import type { JournalDocument } from "@/services/firestore/journals";

// --- Types ---

export type ToggleMode = "shared" | "per-member";

/** 토글 가능한 필드 이름 (일반 + OT) */
export type ToggleField =
  | "problemList"
  | "notes"
  | "improvements"
  | "improvementsNeeded"
  | "bodyAnalysis"
  | "memberGoal"
  | "instructorMemo";

/** 하나의 토글 필드의 상태 */
export interface GroupJournalFieldState {
  mode: ToggleMode;
  shared: string;
  perMember: Record<number, string>; // studiomateMemberId → value
}

/** 그룹 수업 수강생 정보 */
export interface GroupMember {
  studiomateMemberId: number;
  memberName: string;
}

/** 저장용 공통 필드 (모든 회원에게 동일) */
export interface SharedJournalFields {
  classId: string;
  instructorId: string;
  classType?: ClassType;
  isOt: boolean;
  equipments: EquipmentType[];
  exercises: ExerciseEntry[];
}

// --- Constants ---

/** 일반 수업에서 토글 가능한 필드 */
export const TOGGLEABLE_FIELDS: ToggleField[] = [
  "problemList",
  "notes",
  "improvements",
  "improvementsNeeded",
];

/** OT 수업에서 추가로 토글 가능한 필드 */
export const OT_TOGGLEABLE_FIELDS: ToggleField[] = ["bodyAnalysis", "memberGoal", "instructorMemo"];

/** 모든 토글 가능한 필드 */
export const ALL_TOGGLEABLE_FIELDS: ToggleField[] = [...TOGGLEABLE_FIELDS, ...OT_TOGGLEABLE_FIELDS];

// --- Default modes ---

/**
 * 필드별 기본 토글 모드 반환.
 * 모든 필드의 기본값은 "공통(shared)"으로 설정.
 */
export function getDefaultToggleMode(_field: ToggleField): ToggleMode {
  return "shared";
}

// --- Initialization ---

/**
 * 기존 일지 + 회원 목록으로 토글 필드 초기 상태를 결정한다.
 *
 * - 일지가 없으면: 기본 모드 + 빈 값
 * - 모든 회원의 일지가 있고 값이 동일하면: shared 모드
 * - 값이 다르거나 일부 회원만 일지가 있으면: per-member 모드
 */
export function initializeToggleFields(
  existingJournals: Journal[],
  members: GroupMember[],
): Record<ToggleField, GroupJournalFieldState> {
  const journalMap = new Map<number, Journal>();
  for (const j of existingJournals) {
    if (j.studiomateMemberId !== undefined) {
      journalMap.set(j.studiomateMemberId, j);
    }
  }

  const result = {} as Record<ToggleField, GroupJournalFieldState>;

  for (const field of ALL_TOGGLEABLE_FIELDS) {
    const perMember: Record<number, string> = {};
    const values: string[] = [];

    for (const member of members) {
      const journal = journalMap.get(member.studiomateMemberId);
      const value = journal ? ((journal[field] as string | undefined) ?? "") : "";
      perMember[member.studiomateMemberId] = value;
      if (journal) {
        values.push(value);
      }
    }

    // Determine mode
    let mode: ToggleMode;
    let shared = "";

    if (existingJournals.length === 0) {
      // No journals → use default mode
      mode = getDefaultToggleMode(field);
    } else if (values.length < members.length) {
      // Partial journals → per-member (not all members have data)
      mode = "per-member";
    } else {
      // All members have journals — check if values are identical
      const allSame = values.every((v) => v === values[0]);
      if (allSame) {
        mode = "shared";
        shared = values[0] ?? "";
      } else {
        mode = "per-member";
      }
    }

    result[field] = { mode, shared, perMember };
  }

  return result;
}

// --- Toggle switching ---

/**
 * per-member → shared 전환.
 * 첫 번째 회원의 값을 shared로 사용.
 */
export function switchToShared(
  state: GroupJournalFieldState,
  members: GroupMember[],
): GroupJournalFieldState {
  if (state.mode === "shared") return state;

  const firstMemberId = members[0]?.studiomateMemberId;
  const shared = firstMemberId !== undefined ? (state.perMember[firstMemberId] ?? "") : "";

  return {
    mode: "shared",
    shared,
    perMember: state.perMember, // preserve for potential undo
  };
}

/**
 * shared → per-member 전환.
 * shared 값을 모든 회원에 복사.
 */
export function switchToPerMember(
  state: GroupJournalFieldState,
  members: GroupMember[],
): GroupJournalFieldState {
  if (state.mode === "per-member") return state;

  const perMember: Record<number, string> = {};
  for (const member of members) {
    perMember[member.studiomateMemberId] = state.shared;
  }

  return {
    mode: "per-member",
    shared: state.shared,
    perMember,
  };
}

/**
 * per-member 값이 서로 다른지 확인 (전환 시 데이터 유실 위험 판별용)
 */
export function hasDataLossRisk(state: GroupJournalFieldState): boolean {
  if (state.mode !== "per-member") return false;
  const values = Object.values(state.perMember);
  if (values.length === 0) return false;
  return !values.every((v) => v === values[0]);
}

// --- Building journal documents ---

/**
 * 폼 상태에서 N개의 Journal 문서 데이터를 생성한다.
 * Firestore에 저장할 형태 (createdAt/updatedAt 제외).
 */
export function buildJournalDocuments(params: {
  members: GroupMember[];
  toggleFields: Record<ToggleField, GroupJournalFieldState>;
  sharedFields: SharedJournalFields;
}): Array<Omit<JournalDocument, "createdAt" | "updatedAt">> {
  const { members, toggleFields, sharedFields } = params;

  function resolveFieldValue(field: ToggleField, memberId: number): string {
    const state = toggleFields[field];
    if (state.mode === "shared") return state.shared;
    return state.perMember[memberId] ?? "";
  }

  return members.map((member) => {
    const doc: Omit<JournalDocument, "createdAt" | "updatedAt"> = {
      classId: sharedFields.classId,
      instructorId: sharedFields.instructorId,
      studiomateMemberId: member.studiomateMemberId,
      classType: sharedFields.classType,
      isOt: sharedFields.isOt,
      equipments: sharedFields.equipments,
      exercises: sharedFields.exercises,
      problemList: resolveFieldValue("problemList", member.studiomateMemberId),
      improvements: resolveFieldValue("improvements", member.studiomateMemberId),
      improvementsNeeded: resolveFieldValue("improvementsNeeded", member.studiomateMemberId),
    };

    const notesVal = resolveFieldValue("notes", member.studiomateMemberId);
    if (notesVal) doc.notes = notesVal;

    if (sharedFields.isOt) {
      const ba = resolveFieldValue("bodyAnalysis", member.studiomateMemberId);
      if (ba) doc.bodyAnalysis = ba;
      const mg = resolveFieldValue("memberGoal", member.studiomateMemberId);
      if (mg) doc.memberGoal = mg;
      const im = resolveFieldValue("instructorMemo", member.studiomateMemberId);
      if (im) doc.instructorMemo = im;
    }

    return doc;
  });
}

// --- Validation ---

/**
 * 토글 필드의 유효성을 검증한다.
 * 필수 필드가 비어있으면 해당 필드명을 반환.
 */
export function validateToggleFields(
  fields: Record<ToggleField, GroupJournalFieldState>,
  requiredFields: ToggleField[],
): ToggleField[] {
  const errors: ToggleField[] = [];

  for (const field of requiredFields) {
    const state = fields[field];
    if (!state) continue;

    if (state.mode === "shared") {
      if (!state.shared.trim()) {
        errors.push(field);
      }
    } else {
      // per-member: 하나라도 비어있으면 invalid
      const hasEmpty = Object.values(state.perMember).some((v) => !v.trim());
      if (hasEmpty) {
        errors.push(field);
      }
    }
  }

  return errors;
}
